import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Play, Droplets, Building2, Hotel, Landmark, Sparkles, Users, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Section, SectionTitle, FadeIn, StaggerContainer, StaggerItem } from '../components/Section';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

const solutionIcons = [Play, Droplets, Building2, Hotel, Landmark, Sparkles, Users, RefreshCw];

export default function Solutions() {
  const { t, language } = useLanguage();

  const solutions = {
    en: [
      { title: 'Show Fountains', description: 'Choreographed multimedia water features with synchronized music, lighting, and water effects for entertainment venues and landmarks.', features: ['Music synchronization', 'LED lighting', 'DMX-512 control', 'Custom choreography'] },
      { title: 'Dry Deck Fountains', description: 'Flush-mounted fountain systems that create interactive water play areas without standing water pools.', features: ['Interactive play', 'Pedestrian-safe', 'Low maintenance', 'Multi-use spaces'] },
      { title: 'Urban Plaza Fountains', description: 'Public space water features that enhance civic environments and create gathering points for communities.', features: ['Urban integration', 'Public safety', 'Energy efficient', 'Climate adapted'] },
      { title: 'Hotel & Resort Fountains', description: 'Elegant water features for hospitality environments, from grand entrance fountains to pool surrounds.', features: ['Guest experience', 'Ambient design', 'Low noise', 'Premium finishes'] },
      { title: 'Landmark Fountains', description: 'Iconic water features that become defining elements of cityscapes and cultural destinations.', features: ['Monumental scale', 'Artistic vision', 'Engineering excellence', 'Legacy design'] },
      { title: 'Multimedia Water Features', description: 'Advanced fountain systems combining water, light, projection, and sound for immersive experiences.', features: ['Projection mapping', 'Laser integration', 'Sound design', 'Interactive elements'] },
      { title: 'Interactive Fountains', description: 'Sensor-activated and user-responsive water features that engage visitors of all ages.', features: ['Motion sensors', 'Touch controls', 'Real-time response', 'Inclusive design'] },
      { title: 'Renovation & Retrofit', description: 'Modernization and restoration of existing fountain systems with updated technology and improved efficiency.', features: ['System assessment', 'Technology upgrade', 'Energy optimization', 'Heritage preservation'] },
    ],
    fr: [
      { title: 'Fontaines Spectacle', description: "Jeux d'eau multimédia chorégraphiés avec musique, éclairage et effets d'eau synchronisés pour les lieux de divertissement et monuments.", features: ['Synchronisation musicale', 'Éclairage LED', 'Contrôle DMX-512', 'Chorégraphie personnalisée'] },
      { title: 'Fontaines Sèches', description: "Systèmes de fontaines encastrés créant des zones de jeux d'eau interactives sans bassins d'eau stagnante.", features: ['Jeu interactif', 'Sécurité piétonne', 'Faible maintenance', 'Espaces multi-usage'] },
      { title: 'Fontaines de Place Urbaine', description: "Jeux d'eau pour espaces publics qui améliorent les environnements civiques et créent des points de rassemblement.", features: ['Intégration urbaine', 'Sécurité publique', 'Efficacité énergétique', 'Adaptation climatique'] },
      { title: 'Fontaines Hôtel & Resort', description: "Jeux d'eau élégants pour l'hôtellerie, des fontaines d'entrée majestueuses aux contours de piscine.", features: ['Expérience client', 'Design ambiant', 'Faible bruit', 'Finitions premium'] },
      { title: 'Fontaines Monument', description: "Jeux d'eau iconiques qui deviennent des éléments définissant les paysages urbains et destinations culturelles.", features: ['Échelle monumentale', 'Vision artistique', "Excellence d'ingénierie", 'Design patrimonial'] },
      { title: "Jeux d'Eau Multimédia", description: "Systèmes de fontaines avancés combinant eau, lumière, projection et son pour des expériences immersives.", features: ['Mapping de projection', 'Intégration laser', 'Design sonore', 'Éléments interactifs'] },
      { title: 'Fontaines Interactives', description: "Jeux d'eau activés par capteurs et réactifs aux utilisateurs qui engagent les visiteurs de tous âges.", features: ['Capteurs de mouvement', 'Contrôles tactiles', 'Réponse en temps réel', 'Design inclusif'] },
      { title: 'Rénovation & Retrofit', description: "Modernisation et restauration de systèmes de fontaines existants avec technologie mise à jour et efficacité améliorée.", features: ['Évaluation système', 'Mise à niveau techno', 'Optimisation énergie', 'Préservation patrimoine'] },
    ],
    es: [
      { title: 'Fuentes Espectáculo', description: 'Fuentes multimedia coreografiadas con música, iluminación y efectos de agua sincronizados para lugares de entretenimiento y monumentos.', features: ['Sincronización musical', 'Iluminación LED', 'Control DMX-512', 'Coreografía personalizada'] },
      { title: 'Fuentes Secas', description: 'Sistemas de fuentes a ras de suelo que crean áreas de juego de agua interactivas sin piscinas de agua estancada.', features: ['Juego interactivo', 'Seguridad peatonal', 'Bajo mantenimiento', 'Espacios multi-uso'] },
      { title: 'Fuentes de Plaza Urbana', description: 'Fuentes para espacios públicos que mejoran los entornos cívicos y crean puntos de encuentro para las comunidades.', features: ['Integración urbana', 'Seguridad pública', 'Eficiencia energética', 'Adaptación climática'] },
      { title: 'Fuentes Hotel & Resort', description: 'Fuentes elegantes para entornos de hostelería, desde fuentes de entrada majestuosas hasta contornos de piscina.', features: ['Experiencia del huésped', 'Diseño ambiental', 'Bajo ruido', 'Acabados premium'] },
      { title: 'Fuentes Monumento', description: 'Fuentes icónicas que se convierten en elementos definitorios de paisajes urbanos y destinos culturales.', features: ['Escala monumental', 'Visión artística', 'Excelencia de ingeniería', 'Diseño patrimonial'] },
      { title: 'Fuentes Multimedia', description: 'Sistemas de fuentes avanzados que combinan agua, luz, proyección y sonido para experiencias inmersivas.', features: ['Mapeo de proyección', 'Integración láser', 'Diseño de sonido', 'Elementos interactivos'] },
      { title: 'Fuentes Interactivas', description: 'Fuentes activadas por sensores y responsivas al usuario que involucran a visitantes de todas las edades.', features: ['Sensores de movimiento', 'Controles táctiles', 'Respuesta en tiempo real', 'Diseño inclusivo'] },
      { title: 'Renovación & Retrofit', description: 'Modernización y restauración de sistemas de fuentes existentes con tecnología actualizada y eficiencia mejorada.', features: ['Evaluación del sistema', 'Actualización tecnológica', 'Optimización energética', 'Preservación patrimonial'] },
    ],
  };

  const currentSolutions = solutions[language] || solutions.en;

  const industries = {
    en: ['Real Estate Development', 'Hospitality & Tourism', 'Government & Municipal', 'Entertainment Venues'],
    fr: ['Promotion Immobilière', 'Hôtellerie & Tourisme', 'Gouvernement & Municipal', 'Lieux de Divertissement'],
    es: ['Desarrollo Inmobiliario', 'Hostelería & Turismo', 'Gobierno & Municipal', 'Lugares de Entretenimiento'],
  };

  const currentIndustries = industries[language] || industries.en;
  const industryIcons = [Building2, Hotel, Landmark, Play];

  const pageText = {
    en: {
      eyebrow: 'Solutions & Applications',
      title: 'Fountain Solutions for Every Vision',
      subtitle: 'From intimate hotel features to landmark show fountains, we engineer water experiences tailored to your specific application and environment.',
      industriesTitle: 'Industries We Serve',
      industriesSubtitle: 'Tailored Solutions Across Sectors',
      ctaTitle: 'Have a Specific Application in Mind?',
      ctaDesc: "Tell us about your project vision and we'll help you explore the best fountain solution for your specific requirements.",
      ctaBtn: 'Discuss Your Project',
    },
    fr: {
      eyebrow: 'Solutions & Applications',
      title: 'Solutions Fontaines pour Chaque Vision',
      subtitle: "Des fontaines d'hôtel intimes aux fontaines spectacles monumentales, nous concevons des expériences d'eau adaptées à votre application et environnement spécifiques.",
      industriesTitle: 'Industries que Nous Servons',
      industriesSubtitle: 'Solutions Adaptées à Tous les Secteurs',
      ctaTitle: 'Vous Avez une Application Spécifique en Tête ?',
      ctaDesc: 'Parlez-nous de votre vision de projet et nous vous aiderons à explorer la meilleure solution de fontaine pour vos besoins spécifiques.',
      ctaBtn: 'Discuter de Votre Projet',
    },
    es: {
      eyebrow: 'Soluciones y Aplicaciones',
      title: 'Soluciones de Fuentes para Cada Visión',
      subtitle: 'Desde fuentes íntimas de hotel hasta fuentes espectáculo monumentales, diseñamos experiencias de agua adaptadas a su aplicación y entorno específicos.',
      industriesTitle: 'Industrias que Servimos',
      industriesSubtitle: 'Soluciones Adaptadas a Todos los Sectores',
      ctaTitle: '¿Tiene una Aplicación Específica en Mente?',
      ctaDesc: 'Cuéntenos sobre su visión de proyecto y le ayudaremos a explorar la mejor solución de fuente para sus requisitos específicos.',
      ctaBtn: 'Discutir Su Proyecto',
    },
  };

  const text = pageText[language] || pageText.en;

  return (
    <main data-testid="solutions-page">
      <SEO page="solutions" />
      {/* Hero Section */}
      <section className="relative py-24 lg:py-32 hero-gradient overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 right-20 w-80 h-80 rounded-full bg-[#29ABE2]/5 blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="text-sm font-medium uppercase tracking-widest text-[#29ABE2] mb-4 block">
              {text.eyebrow}
            </span>
            <h1 className="text-5xl lg:text-6xl font-bold text-[#1A1A1A] tracking-tight uppercase mb-6">
              {text.title}
            </h1>
            <p className="text-xl text-[#6C757D] leading-relaxed">
              {text.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Solutions Grid */}
      <Section background="white">
        <StaggerContainer className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {currentSolutions.map((solution, index) => {
            const Icon = solutionIcons[index] || Play;
            const images = [
              'https://images.unsplash.com/photo-1638477403890-23ca3f3b5bf2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxzaG93JTIwZm91bnRhaW4lMjBuaWdodCUyMHdhdGVyJTIwbGlnaHRzJTIwc3BlY3RhY3VsYXJ8ZW58MHx8fHwxNzczODc2OTcxfDA&ixlib=rb-4.1.0&q=85',
              'https://static.prod-images.emergentagent.com/jobs/74b5b0bb-dd03-4c31-97c1-4a19e20989fc/images/fa6666faa2ceabd503dd7c12fd9bb03329127ef1366464766c804d4f125022cc.png',
              'https://images.unsplash.com/photo-1760256996984-f08a61eb08c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHx1cmJhbiUyMHBsYXphJTIwZm91bnRhaW4lMjBtb2Rlcm4lMjBjaXR5JTIwc3F1YXJlfGVufDB8fHx8MTc3Mzg3Njk3M3ww&ixlib=rb-4.1.0&q=85',
              'https://images.unsplash.com/photo-1736203301099-4602b44f2621?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBob3RlbCUyMGZvdW50YWluJTIwcmVzb3J0JTIwZW50cmFuY2UlMjBlbGVnYW50fGVufDB8fHx8MTc3Mzg3Njk3NXww&ixlib=rb-4.1.0&q=85',
              'https://images.unsplash.com/photo-1772864463667-5288e50605a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxsYW5kbWFyayUyMG1vbnVtZW50YWwlMjBmb3VudGFpbiUyMGljb25pYyUyMGNpdHlzY2FwZXxlbnwwfHx8fDE3NzM4NzY5ODZ8MA&ixlib=rb-4.1.0&q=85',
              'https://images.unsplash.com/photo-1574593651228-a2fa3148ec8f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxmb3VudGFpbiUyMHByb2plY3Rpb24lMjBtYXBwaW5nJTIwbGlnaHQlMjBzaG93JTIwbXVsdGltZWRpYSUyMHdhdGVyfGVufDB8fHx8MTc3Mzg3Njk4N3ww&ixlib=rb-4.1.0&q=85',
              'https://images.unsplash.com/photo-1568971316801-980eea553e6e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxpbnRlcmFjdGl2ZSUyMGZvdW50YWluJTIwY2hpbGRyZW4lMjBwbGF5aW5nJTIwd2F0ZXIlMjBqZXRzJTIwZ3JvdW5kfGVufDB8fHx8MTc3Mzg3Njk4OHww&ixlib=rb-4.1.0&q=85',
              'https://static.prod-images.emergentagent.com/jobs/74b5b0bb-dd03-4c31-97c1-4a19e20989fc/images/42f4fe8067d3bb2388a62b4379b25253d5062a44f155bd695f2e58e6b306ade9.png',
            ];
            return (
              <StaggerItem key={index}>
                <div className="service-card bg-white border border-gray-100 overflow-hidden group h-full">
                  <div className="aspect-[16/9] overflow-hidden relative">
                    <img
                      src={images[index] || images[0]}
                      alt={solution.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/60 to-transparent" />
                    <div className="absolute bottom-4 left-4 flex items-center gap-2">
                      <div className="w-10 h-10 bg-[#29ABE2] rounded-full flex items-center justify-center">
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  </div>
                  <div className="p-8">
                    <h3 className="text-xl font-bold text-[#1A1A1A] mb-3 uppercase tracking-wide">
                      {solution.title}
                    </h3>
                    <p className="text-sm text-[#6C757D] leading-relaxed mb-4">
                      {solution.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {solution.features.map((feature, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 text-xs bg-[#F8F9FA] text-[#6C757D] rounded-full"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </StaggerItem>
            );
          })}
        </StaggerContainer>
      </Section>

      {/* Industries Served */}
      <Section background="surface">
        <SectionTitle
          eyebrow={text.industriesTitle}
          title={text.industriesSubtitle}
          align="center"
        />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {currentIndustries.map((industry, index) => {
            const Icon = industryIcons[index] || Building2;
            return (
              <FadeIn key={index} delay={index * 0.1}>
                <div className="text-center p-6 bg-white border border-gray-100">
                  <div className="w-12 h-12 bg-[#29ABE2]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-6 h-6 text-[#29ABE2]" />
                  </div>
                  <h4 className="text-sm font-bold text-[#1A1A1A] uppercase tracking-wide">
                    {industry}
                  </h4>
                </div>
              </FadeIn>
            );
          })}
        </div>
      </Section>

      {/* CTA Section */}
      <Section background="dark" paddingY="py-20 lg:py-28">
        <div className="text-center max-w-3xl mx-auto">
          <FadeIn>
            <h2 className="text-4xl lg:text-5xl font-bold text-white tracking-tight uppercase mb-6">
              {text.ctaTitle}
            </h2>
            <p className="text-lg text-gray-400 mb-8">
              {text.ctaDesc}
            </p>
            <Link to="/contact">
              <Button
                data-testid="solutions-cta-btn"
                className="bg-[#29ABE2] text-white hover:bg-[#0077B6] rounded-none px-8 py-6 text-sm font-bold uppercase tracking-wider"
              >
                {text.ctaBtn}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </FadeIn>
        </div>
      </Section>
    </main>
  );
}
